/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2015-2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.jwplayerModule')

/**
   * Our directive that handles the video and audio (only for desktop) options for our app.
   *
   * Our wrapper for jwplayer 7.  Can handle our custom videos as well as youtube.  Currently only supports html5.
   * FUTURE: Enable rtmp streaming if supported (i.e. Flash)
   */

  .directive('bulbJwplayer', [
    '$q',
    '$timeout',
    'BulbBrowserService',
    'BulbPageService',
    'CKE_STATUS',
    'JWPLAYER',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    bulbJwplayer,
  ]);

function bulbJwplayer(
  $q,
  $timeout,
  BulbBrowserService,
  BulbPageService,
  CKE_STATUS,
  JWPLAYER,
) {
  return {
    restrict: 'E',
    require: ['^?mediumContentEditor', '^?contentBlock', '^?contentEditor'],
    scope: {
      video: '=',
      audio: '=',
      playerId: '=',
      splashImageUrl: '=',
    },
    link(scope, element, attrs, ctrls) {
      let contentEditorCtrl = ctrls[0];
      const contentBlockCtrl = ctrls[1];

      if (BulbPageService.page && BulbPageService.page.unit
        && BulbPageService.page.unit.ckeStatus === CKE_STATUS.UPGRADED) {
        // eslint-disable-next-line prefer-destructuring
        contentEditorCtrl = ctrls[2];
      }

      if (!angular.isString(attrs.video) && !angular.isString(attrs.audio)) {
        throw new Error('bulb-jwplayer requires a video or audio attribute');
      }

      element.on('click', (event) => {
        event.stopPropagation();
      });

      const pid = window.jwplayer.defaults.pid;
      const key = window.jwplayer.defaults.key;

      window.jwplayer.defaults = JWPLAYER.defaultOptions;

      window.jwplayer.defaults.key = key;
      window.jwplayer.defaults.pid = pid;

      /**
       * Function that checks to see if the jwplayer instance is an audio player.
       * @type {Boolean} Whether or not the jwplayer instance is an audio player
       */
      function isJwPlayerAudioType(sources) {
        let foundMp3 = false;
        for (let c = 0; c < sources.length; c += 1) {
          if (sources[c].type === 'mp3') {
            foundMp3 = true;
            break;
          }
        }
        return foundMp3;
      }

      /**
       * Function that returns the specified jwplayer controls container. This could be the timeline scrubber,
       * volume slider, etc...
       * @param containerName The specified controls container name. Example: 'center' is the scrubber timeline
       * and 'right' are the volume controls.
       *
       * @return The specified container.
       */
      function grabJwPlayerContainer(containerName) {
        return jQuery(`#content-block-${contentBlockCtrl.contentBlock.id}`).find('.jw-controls')
          .find('.jw-controlbar').find(`.jw-controlbar-${containerName}-group`);
      }

      /**
       * Function that returns the parent controls container for jwplayer.
       * @return The parent controls container housing all other controls.
       */
      function grabJwPlayerControlsContainer() {
        return jQuery(`#content-block-${contentBlockCtrl.contentBlock.id}`).find('.jw-controls');
      }

      /**
       * Function that returns the splash image for the instant play video
       * @param videoUrl
       * @returns {*}
       */
      function captureSplashImage(videoUrl) {
        const deferred = $q.defer();
        const videoElement = document.createElement('video');
        videoElement.src = `${videoUrl}#t=0.5`;
        videoElement.crossOrigin = 'anonymous';
        videoElement.onloadedmetadata = () => {
          videoElement.width = videoElement.videoWidth;
          videoElement.height = videoElement.videoHeight;
          // If the video is at least 5 seconds long, then the frame at the 5 second mark should be used.
          if (videoElement.duration >= 5) {
            videoElement.currentTime = 5;
          }
          videoElement.onloadeddata = () => {
            const canvas = document.createElement('canvas');
            canvas.width = videoElement.videoWidth;
            canvas.height = videoElement.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
            const imageUrl = canvas.toDataURL('image/jpg');
            deferred.resolve(imageUrl);
          };
        };
        return deferred.promise;
      }

      let deregisterVideoHandler = null;

      function initVideoPlayer(video, playerId, splashImageUrl) {
        let videoPlayer = null;

        function initPlayer(thisPlayerId, videoArray, splashImage) {
          $(element).empty();
          element.append(`<div id="${thisPlayerId}"></div>`);

          videoPlayer = window.jwplayer(thisPlayerId);

          videoPlayer.setup({
            image: splashImage,
            sources: videoArray,
          });

          /**
           * On FireFox, the height inherently applied by JWPlayer messes up the visibility of the preview image.
           * Need to make it the container height instead.
           */
          if (BulbBrowserService.isFireFox() && thisPlayerId !== 'assetlibrary-detail-modal-player') {
            $timeout(() => {
              element.find(`div[id="${thisPlayerId}"]`).css('height', element[0].style.height);
            }, JWPLAYER.defaultOptions.timeout);
          }
        }

        if (deregisterVideoHandler) {
          deregisterVideoHandler();
        }

        let videoArray = [];
        switch (video.jspType) {
          case 'TRANSCODING':
            videoArray.push({
              file: video.originalAssetUrl,
            });

            captureSplashImage(video.originalAssetUrl).then((url) => {
              initPlayer(playerId, videoArray, url);
            });
            break;
          case 'ASSET':
            video.transcodedAssets.forEach((v) => {
              // Will probably need to test this periodically, but as of October 2016, chromeos
              // seems to have playback issues with videos when the lesser dimension is greater
              // than 620.
              // Testing consisted of taking HD portrait and landscape videos and reducing
              // dimensions until chromeos could play videos without issue.
              if (Math.min(v.width, v.height) > 620 && BulbBrowserService.isChromeBook()) {
                return;
              }

              videoArray.push({
                file: v.url,
                label: v.height,
              });
            });

            videoArray = videoArray.sort((a, b) => a.label - b.label).reverse();
            videoArray[0] = {
              file: videoArray[0].file,
              label: videoArray[0].label,
              default: 'true',
            };

            initPlayer(playerId, videoArray, splashImageUrl || video.splashImage.url);
            break;
          default:
            throw new Error(`Unknown type: ${video.jspType}`);
        }

        deregisterVideoHandler = scope.$on('bulb-video-play-toggle', (event, _playerId) => {
          if (playerId === _playerId) {
            videoPlayer.play();
          }
        });
      }

      // Function that initializes the audio player with the transcoded audio assets
      function initAudioPlayer(audio, playerId) {
        let audioPlayer = null;
        const audioArray = [];

        audio.transcodedAssets.forEach((audioAsset) => {
          audioArray.push({
            file: audioAsset.url,
            bitrate: audioAsset.bitrate,
          });
        });

        function initPlayer(thisPlayerId, thisAudioArray) {
          element.empty();
          element.append(`<div id="${thisPlayerId}"></div>`);

          audioPlayer = window.jwplayer(thisPlayerId);

          audioPlayer.setup({
            sources: thisAudioArray,
            height: '40px',
          });
        }

        initPlayer(playerId, audioArray);

        $timeout(() => {
          // Allow the user to click anywhere on the audio block to display the selected bulb-frame and
          // toolbar
          if (contentBlockCtrl && contentEditorCtrl) {
            const $contentBlockElement = jQuery(`#${contentBlockCtrl.contentBlock.id}`);

            // Initial configuration of the jwplayer state
            // Grab the scrubber container
            const $slideBar = grabJwPlayerContainer('center');
            // Grab the volume container
            const $volume = grabJwPlayerContainer('right');

            // Disallow pointer events on the scrubber timeline and volume slider
            $slideBar.css('pointer-events', 'none');
            $volume.css('pointer-events', 'none');

            // Allow the user to click anywhere on jwplayer controls container
            const $controlsContainer = grabJwPlayerControlsContainer();
            $controlsContainer.click(() => {
              if ($contentBlockElement[0]) {
                contentEditorCtrl.insertBlockExtension.selectBlock($contentBlockElement[0]);
              }
            });

            // Listen for play events and pause all other instances
            audioPlayer.on('play', function playCallback() {
              $slideBar.css('pointer-events', '');
              $volume.css('pointer-events', '');
              if ($contentBlockElement[0]) {
                contentEditorCtrl.insertBlockExtension.selectBlock($contentBlockElement[0]);
              }
              const jwPlayers = jQuery('.jwplayer');
              for (let i = 0; i < jwPlayers.length; i++) {
                const currentJwPlayer = jwPlayers[i];
                const currentJwPlayerSources = window.jwplayer(currentJwPlayer).getConfig().sources;
                const isAudioPlayer = isJwPlayerAudioType(currentJwPlayerSources);
                if (currentJwPlayer.id !== this.id && isAudioPlayer) {
                  window.jwplayer(currentJwPlayer).play(false);
                }
              }
            });

            // Listen for pause events to re-enable scrubbing on timeline and volume
            audioPlayer.on('pause', () => {
              $slideBar.css('pointer-events', 'none');
              $volume.css('pointer-events', 'none');
            });
          }
        }, JWPLAYER.defaultOptions.timeout);
      }

      if (scope.video) {
        initVideoPlayer(scope.video, scope.playerId, scope.splashImageUrl);
      } else if (scope.audio && scope.audio.jspType !== 'TRANSCODING') {
        initAudioPlayer(scope.audio, scope.playerId);
      }

      scope.$watchGroup(['video.id', 'video.splashImage.url'], () => {
        if (scope.video) {
          initVideoPlayer(scope.video, scope.playerId, scope.splashImageUrl);
        }
      });
    },
  };
}
